import { useEffect, useState } from "react";
import { ArrowUp } from "lucide-react";
import { cn } from "@/lib/utils";

interface ScrollToTopButtonProps {
  /** Pixel scroll offset before the button appears. */
  threshold?: number;
  className?: string;
}

/**
 * Floating, accessibility-friendly "scroll to top" button.
 * - Appears after the user scrolls past `threshold` pixels.
 * - Fixed bottom-right; small circular, subtle styling (not a primary CTA).
 * - Keyboard focusable with visible focus ring; smooth-scrolls to top.
 */
export function ScrollToTopButton({ threshold = 320, className }: ScrollToTopButtonProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > threshold);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);

  const handleClick = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label="Scroll to top"
      title="Scroll to top"
      tabIndex={visible ? 0 : -1}
      aria-hidden={!visible}
      className={cn(
        "fixed bottom-6 right-6 z-50 h-10 w-10 rounded-full",
        "bg-secondary text-secondary-foreground border border-border shadow-md",
        "flex items-center justify-center",
        "transition-opacity duration-200 hover:bg-accent hover:text-accent-foreground",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        visible ? "opacity-100" : "opacity-0 pointer-events-none",
        className,
      )}
    >
      <ArrowUp className="h-4 w-4" />
    </button>
  );
}